// CORE
// Este é um arquivo core do boilerplate e modificações neste arquivo serão bloqueadas via pré-hook de commit.
// Fazemos este bloqueio para manter a consistência do boilerplate e diminuir as chances de conflitos nos updates.
// Caso possível, procure uma alternativa ao uso deste arquivo.
// Se a alteração for realmente necessária ou for um update do boilerplate,
// realize o commit usando o comando 'git commit --no-verify'.

import React, { ReactNode } from 'react';

import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { cleanLocalStorageValues } from '../components/generic/forms/Form';
import { Loading } from '../components/generic/Loading';
import { PERMISSION_GROUP_MAP, Permission, Role } from './permissions';

interface User {
  id?: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
  role?: Role;
  tenant_id?: number | null;
  tenant_count?: number;
  tenant_name?: string | null;
}

interface AuthPageGateProps {
  children: ReactNode;
  permissions?: Permission | Permission[];
  isPublic?: boolean;
}

// interface TenantSelectionPageGateProps {
//   children: ReactNode;
//   isPublic?: boolean;
// }

interface CompletarPerfilPageGateProps {
  children: ReactNode;
  isPublic?: boolean;
}

interface AuthGateProps {
  children: ReactNode;
  permissions?: Permission | Permission[];
}

interface RoleGateProps {
  children: ReactNode;
  allowedRoles: Role | Role[];
  fallback?: ReactNode;
}

export function AuthPageGate({
  children,
  permissions,
  isPublic,
}: AuthPageGateProps) {
  const router = useRouter();
  const { hasPermission, user, status } = usePermissions(permissions);
  const isLogin = router.pathname.startsWith('/auth');
  const { data: session } = useSession();

  React.useEffect(() => {
    if (status === 'loading' || isPublic) return;

    if (isLogin && user) {
      router.push((router.query?.callbackUrl as string) ?? '/');
      return;
    }

    if (isLogin) {
      cleanLocalStorageValues();
    }

    if (!user && !isLogin) {
      cleanLocalStorageValues();
      router.push(
        '/auth/login?error=SessionTimeout&callbackUrl=' +
          (router.asPath ? encodeURI(router.asPath) : '/'),
      );
      return;
    }
    if (!hasPermission) {
      router.push('/auth/error');
      return;
    }
  }, [status, hasPermission, user, isPublic, router, isLogin, session]);

  if (isLogin && status === 'authenticated') {
    router.replace((router.query?.callbackUrl as string) ?? '/');
    return <Loading fullScreen />;
  }

  // Páginas públicas liberadas
  if (isPublic) return children;

  // Login ainda pode renderizar enquanto carrega
  if (isLogin && status === 'loading') {
    return <Loading fullScreen />;
  }

  // Protege páginas privadas
  if (status !== 'authenticated') {
    return <Loading fullScreen />;
  }

  // Se há permissões requeridas, verifica antes de renderizar
  if (permissions && !hasPermission) {
    return <Loading fullScreen />;
  }

  return children;
}

// export function TenantSelectionPageGate({
//   children,
//   isPublic,
// }: TenantSelectionPageGateProps) {
//   const router = useRouter();
//   const { data: userSession } = useSession();
//   if (
//     !router.pathname.startsWith('/auth/tenant-select') &&
//     !router.pathname.startsWith('/completar-cadastro') && // precisa tirar para não causar loop infinito
//     userSession?.user &&
//     userSession?.user?.tenant_id == null &&
//     !isPublic
//   ) {
//     router.push(`/auth/tenant-select?callbackUrl=` + encodeURI(router.asPath));
//     return null;
//   }
//   return children;
// }

export function CompletarPerfilPageGate({
  children,
  isPublic,
}: CompletarPerfilPageGateProps) {
  const router = useRouter();
  const { data: userSession } = useSession();

  if (isPublic || !userSession?.user) {
    return children;
  }

  // Adicionar aqui condições extras para completar o cadastro que sejam relevantes para cada empresa
  if (!userSession?.user?.name || !userSession?.user?.image) {
    if (router.pathname.startsWith('/completar-cadastro')) {
      return children;
    }
    router.push(`/completar-cadastro?callbackUrl=` + encodeURI(router.asPath));
    return null;
  }
  if (!router.pathname.startsWith('/completar-cadastro')) {
    return children;
  }
  router.push('/');
  return null;
}

export function AuthGate({ children, permissions }: AuthGateProps) {
  const { hasPermission, status } = usePermissions(permissions);

  if (status != 'authenticated') return null;

  if (hasPermission) return children;
  return null;
}

/**
 * Componente para proteger containers/seções baseado em roles específicas.
 * Use quando a lógica é simplesmente "só X role pode ver isso".
 *
 * Para funcionalidades reutilizáveis, prefira usar AuthGate com permissões.
 *
 * @example
 * // Container só para PREMIUM e CLIENT
 * <RoleGate allowedRoles={[ROLE_PREMIUM, ROLE_CLIENT]}>
 *   <PremiumFeature />
 * </RoleGate>
 *
 * @example
 * // Container só para CLIENT
 * <RoleGate allowedRoles={ROLE_CLIENT}>
 *   <ClientOnlySection />
 * </RoleGate>
 */
export function RoleGate({
  children,
  allowedRoles,
  fallback = null,
}: RoleGateProps) {
  const { data: session, status } = useSession();

  if (status !== 'authenticated') {
    return <>{fallback}</>;
  }

  const user = session?.user as User | undefined;
  if (!user?.role) {
    return <>{fallback}</>;
  }

  const rolesArray = Array.isArray(allowedRoles)
    ? allowedRoles
    : [allowedRoles];
  const hasRole = rolesArray.includes(user.role);

  if (hasRole) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
}

interface UsePermissionsReturn {
  hasPermission: boolean;
  user: User | null | undefined;
  status: 'loading' | 'authenticated' | 'unauthenticated';
}

export function usePermissions(
  permissions?: Permission | Permission[],
): UsePermissionsReturn {
  const { data: session, status } = useSession();
  if (status == 'loading')
    return {
      hasPermission: false,
      user: null,
      status: status,
    };

  const user = session?.user as User | undefined;

  return {
    hasPermission: userHasPermission(user, permissions),
    user: user,
    status: status,
  };
}

function userHasPermission(
  user: User | null | undefined,
  requiredPermissions?: Permission | Permission[],
): boolean {
  if (!requiredPermissions) return true;

  let permissionsArray: Permission[] = Array.isArray(requiredPermissions)
    ? requiredPermissions
    : [requiredPermissions];

  permissionsArray = permissionsArray.filter((permission) => !!permission);

  if (permissionsArray.length == 0) return true;
  if (!user?.role) return false;

  return permissionsArray.some((permission) => {
    return PERMISSION_GROUP_MAP[permission].includes(user.role as Role);
  });
}

/**
 * Hook para verificar se o usuário tem uma role específica.
 * Útil para lógica condicional baseada em roles.
 *
 * @example
 * const { hasRole, userRole } = useRole();
 * if (hasRole(ROLE_PREMIUM)) {
 *   // fazer algo
 * }
 */
export function useRole() {
  const { data: session, status } = useSession();
  const user = session?.user as User | undefined;

  const hasRole = (role: Role | Role[]): boolean => {
    if (status !== 'authenticated' || !user?.role) {
      return false;
    }

    const rolesArray = Array.isArray(role) ? role : [role];
    return rolesArray.includes(user.role);
  };

  return {
    hasRole,
    userRole: user?.role,
    isAuthenticated: status === 'authenticated',
  };
}
